<template>
  <BaseTable
    v-if="priceCatalogOrderItems"
    :headers="priceCatalogOrderHeaders"
    :list="priceCatalogOrderItems"
    :selected-row-id="selectedRowId"
    :hide-grouping="true"
    :is-add-new-hidden="mode == 'view'"
    :hide-multiselect-delete="mode == 'view'"
    @add-new="onAddNewCatalogOrder"
    @deleteMultiselect="onDeleteMultiselect"
    @refresh-data="emit('refresh')"
    @table-item-clicked="onOrderListClicked"
    clickable
    table-id="priceCatalogOrders">
    <template #actions="{ item }">
      <div class="flex items-center justify-center gap-1" v-if="mode == 'edit'">
        <button
          class="rounded-full p-1.5 text-theme-500 hover:bg-theme-50"
          @click="onRowEditClick(item as PricingPolicyCatalogOrderHeader)">
          <IconPencilSquare class="size-4" />
        </button>
        <button
          v-if="item.isFromManager"
          type="button"
          class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
          @click="deleteSingleConfirm(item as PricingPolicyCatalogOrderHeader)">
          <IconTrashOutline class="size-4" />
        </button>
      </div>
    </template>
  </BaseTable>

  <BaseModalConfirm
    v-if="modalDeleteRow"
    @cancel="modalDeleteRow = false"
    @confirm="onDeleteConfirm" />

  <BaseModalEditCreate
    v-if="modalAddEditHeader && priceCatalogOrder"
    :mode="modalMode"
    :is-modified="isModified"
    :title="
      modalMode == 'add'
        ? $t('pricing-catalogs.addNewCatalogPolicyTitle')
        : $t('pricing-catalogs.editCatalogPolicyTitle')
    "
    max-width="narrow"
    @dismiss-changes="modalAddEditHeader = false"
    @save="onEditHeader">
    <template #form>
      <div>
        <label for="code" class="text-sm font-medium">{{ $t('baseObject.code') }}</label>
        <BaseInput
          v-model="priceCatalogOrder.code"
          color="light"
          class="h-8 text-sm"
          show-error-as-tooltip
          :error="$v.code.required.$invalid ? $v.code.required.$message : ''">
        </BaseInput>
      </div>
      <div class="py-4">
        <label for="name" class="text-sm font-medium">{{ $t('baseObject.name') }}</label>
        <BaseInput
          v-model="priceCatalogOrder.name"
          color="light"
          class="h-8 text-sm"
          show-error-as-tooltip
          :error="$v.name.required.$invalid ? $v.name.required.$message : ''">
        </BaseInput>
      </div>
      <div>
        <label class="text-sm font-medium">{{ $t('pricing-catalogs.company') }}</label>
        <BaseSelectDropdown
          class="text-sm"
          size="sm"
          :items="dropdownCompanies"
          :selected-item="selectedCompany"
          @update:selected="handleCompanySelection"
          name="company"
          mode="edit"
          clearable
          show-error-as-tooltip
          :error="$v.company.required.$invalid ? $v.company.required.$message : ''" />
      </div>
      <div>
        <label for="status" class="text-sm font-medium">{{ $t('pricing-catalogs.status') }}</label>
        <BaseToggle
          v-model="priceCatalogOrder.isActive"
          name="status"
          label="Active"
          class="text-sm">
        </BaseToggle>
      </div>
    </template>
    <template #saveButtonText>{{ modalMode == 'add' ? 'Add' : 'Edit' }}</template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import useVuelidate from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import BaseTable from '../common/BaseTable.vue';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import BaseToggle from '../common/BaseToggle.vue';
import IconPencilSquare from '../../assets/icons/IconPencilSquare.vue';
import IconTrashOutline from '../../assets/icons/IconTrashOutline.vue';

import { type SelectDropdownItem, RecordMode } from '../../types/Base';
import { type PricingPolicyCatalogOrderHeader, type PricingPolicy } from '../../types/PricingTypes';

import { useLookupsStore } from '../../stores/lookups';
import { helpers, required } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';

const { t: $t } = useI18n({ useScope: 'global' });
const { dropdownCompanies } = storeToRefs(useLookupsStore());
const { getCompaniesLookup } = useLookupsStore();
const priceCatalogOrderHeaders = [
  { value: 'code', text: 'Code', type: 'text', active: true },
  { value: 'name', text: 'Name', type: 'text', active: true },
  { value: 'isActive', text: 'Status', type: 'status', active: true },
  { value: 'actions', text: 'Actions', active: true },
];

const selectedCompany = ref<SelectDropdownItem | null>(null);
const modalMode = ref<'add' | 'edit'>('add');
const modalAddEditHeader = ref(false);
const modalDeleteRow = ref(false);
const priceCatalogOrder = ref<PricingPolicyCatalogOrderHeader>();
const initialPriceCatalogOrder = ref<PricingPolicyCatalogOrderHeader>();
let deleteRowItem: PricingPolicyCatalogOrderHeader | undefined = undefined;

defineProps<{
  mode: 'view' | 'edit';
  priceCatalogOrderItems: PricingPolicyCatalogOrderHeader[];
  selectedRowId: string;
}>();

const emit = defineEmits(['addedHeader', 'modifiedHeader', 'refresh', 'itemSelected']);

onMounted(async () => {
  await getCompaniesLookup();
});

function initDropdowns() {
  if (priceCatalogOrder.value) {
    const companyValue = dropdownCompanies.value.find(
      (item) => item.value == priceCatalogOrder.value?.pricingPolicy?.companyId
    );
    if (companyValue) selectedCompany.value = companyValue;
    else if (dropdownCompanies.value?.[0]) handleCompanySelection(dropdownCompanies.value?.[0]);
  }
}

function handleCompanySelection(item: SelectDropdownItem) {
  selectedCompany.value = item ? item : null;
  if (priceCatalogOrder.value?.pricingPolicy)
    priceCatalogOrder.value.pricingPolicy.companyId = item ? item.value.toString() : '';
}

function onAddNewCatalogOrder() {
  modalMode.value = 'add';
  modalAddEditHeader.value = true;
  const pricingPolicy: PricingPolicy = {
    id: uuidv4(),
    companyId: '',
    pricingType: 0,
    isActive: true,
    isFromManager: true,
  };
  priceCatalogOrder.value = {
    id: uuidv4(),
    pricingPolicyId: pricingPolicy.id,
    pricingPolicy: pricingPolicy,
    name: '',
    code: '',
    isActive: true,
    isFromManager: true,
  } as PricingPolicyCatalogOrderHeader;
  initDropdowns();
  initialPriceCatalogOrder.value = deepCopy<PricingPolicyCatalogOrderHeader>(
    priceCatalogOrder.value
  );
}

function onRowEditClick(item: PricingPolicyCatalogOrderHeader) {
  modalMode.value = 'edit';
  modalAddEditHeader.value = true;
  initialPriceCatalogOrder.value = deepCopy<PricingPolicyCatalogOrderHeader>(item);
  priceCatalogOrder.value = deepCopy<PricingPolicyCatalogOrderHeader>(item);
  initDropdowns();
}

function onEditHeader() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  modalAddEditHeader.value = false;
  if (priceCatalogOrder.value) {
    if (modalMode.value == 'edit') {
      priceCatalogOrder.value = { ...priceCatalogOrder.value, recordMode: RecordMode.Modified };
      emit('modifiedHeader', priceCatalogOrder.value);
    } else {
      priceCatalogOrder.value = { ...priceCatalogOrder.value, recordMode: RecordMode.Added };
      emit('addedHeader', priceCatalogOrder.value);
    }
  }

  $v.value.$reset();
}

function onDeleteMultiselect(items: PricingPolicyCatalogOrderHeader[]) {
  items.forEach((header) => {
    header = { ...header, deleted: true };
    emit('modifiedHeader', header);
  });
}

function deleteSingleConfirm(item: PricingPolicyCatalogOrderHeader) {
  deleteRowItem = item;
  modalDeleteRow.value = true;
}

function onDeleteConfirm() {
  modalDeleteRow.value = false;
  if (deleteRowItem) {
    deleteRowItem = { ...deleteRowItem, deleted: true };
    emit('modifiedHeader', deleteRowItem);
  }
}

function onOrderListClicked(item: PricingPolicyCatalogOrderHeader) {
  emit('itemSelected', item);
}

const isModified = ref<boolean>(false);
watch(
  () => priceCatalogOrder.value,
  (newData) => {
    validationModel.value.name = newData?.name || '';
    validationModel.value.code = newData?.code || '';
    validationModel.value.company = newData?.pricingPolicy?.companyId || '';
    isModified.value = JSON.stringify(newData) !== JSON.stringify(initialPriceCatalogOrder.value);
    $v.value.$touch();
  },
  { deep: true }
);

const validationModel = ref({
  code: priceCatalogOrder.value?.code || '',
  name: priceCatalogOrder.value?.name || '',
  company: priceCatalogOrder.value?.pricingPolicy?.companyId || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  code: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  company: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
